import type { AABB2 } from "geom/aabb2Types";
import * as THREE from "three";
import { makeLine } from "./convert";
import type { HighlightDataTexture } from "./HighlightDataTexture";
import { OverlayScene } from "./OverlayScene";
import { threeGrid2DCreate } from "./threeGrid2DCreate";
import type { EffectComposerPass } from "./threejs/examples/jsm/postprocessing/EffectComposerPass";
import { clearThreeScene, freeThreeObject3D } from "./threejs/higharc/clearThreeScene";
import { SSFXRendererLayerUniforms } from "./threejs/higharc/LayerSpecificUniforms";
import SSFXRendererScene from "./threejs/higharc/SSFXRendererScene";
import type { HasMarkRendererForUpdate, SSFXRendererSceneContainer } from "./threejs/higharc/SSFXRendererTypes";

export const defaultSceneBackgroundColor = new THREE.Color(0xffffff);
export const defaultSceneDarkModeBackgroundColor = new THREE.Color(0x111111);
export const sheetBackgroundColor = new THREE.Color("lightgrey");

const groundPlaneGeo = new THREE.PlaneGeometry(19200, 19200);
groundPlaneGeo.userData.isCached = true;

/**
 * A stack of scenes, consisting of a main scene and potentially overlay scenes that are rendered on top of it.
 *
 * Keeps track of scene changes so that views can be re-rendered as appropriate.
 */
export class THREEViewerScene implements SSFXRendererSceneContainer {
  readonly ssfxRendererScene: SSFXRendererScene | undefined;
  readonly scene: THREE.Scene;
  readonly layerSpecificUniforms: SSFXRendererLayerUniforms;
  private readonly overlayScenes: OverlayScene[];

  readonly viewers: HasMarkRendererForUpdate[] = [];

  /** Group that's added to the scene automatically and not deleted when clear() is called. */
  readonly staticContent: THREE.Group;

  private background: THREE.Color | null = defaultSceneBackgroundColor;
  private darkModeBackground: THREE.Color | null | undefined = defaultSceneDarkModeBackgroundColor;

  private gridParent: THREE.Group | undefined;
  private grid2DMaterial: THREE.ShaderMaterial | undefined;

  public grid: THREE.Object3D | null = null;

  private groundPlane: THREE.Mesh | undefined;

  private axesHelper: THREE.Object3D | undefined;

  private generationInternal: number = 0;

  highlightDataTexture: HighlightDataTexture | undefined;

  private darkMode: boolean = false;

  get generation() {
    return this.generationInternal;
  }

  constructor(useSsfxRendererScene: boolean, darkMode: boolean) {
    this.scene = new THREE.Scene();
    this.scene.matrixWorldAutoUpdate = false;
    this.staticContent = new THREE.Group();
    this.scene.add(this.staticContent);
    this.layerSpecificUniforms = new SSFXRendererLayerUniforms();
    if (useSsfxRendererScene) {
      this.ssfxRendererScene = new SSFXRendererScene({
        scene: this.scene,
        layerSpecificUniforms: this.layerSpecificUniforms,
        container: this,
      });
      this.staticContent.add(this.ssfxRendererScene.sceneLightsManager.group);
      this.staticContent.add(this.ssfxRendererScene.refractionProbeGroup);
    }
    this.overlayScenes = [];
    this.darkMode = darkMode;
  }

  /** Call this to let interactive viewers know that they need to re-render the scene. */
  markRenderingNeedsUpdate() {
    ++this.generationInternal;
    for (const viewer of this.viewers) {
      viewer.markRendererForUpdate();
    }
  }

  markShadowMapsForUpdate() {
    if (this.ssfxRendererScene) {
      this.ssfxRendererScene.markShadowMapsForUpdate();
    }
  }

  updateLightProbesList() {
    if (this.ssfxRendererScene) {
      this.ssfxRendererScene.updateLightProbesList();
    }
  }

  set grid2DVisible(visible: boolean) {
    if (visible && !this.gridParent) {
      this.gridParent = new THREE.Group();
      this.grid2DMaterial = threeGrid2DCreate(this.gridParent, -0.2);
      this.staticContent.add(this.gridParent);
    }
    if (this.gridParent) {
      this.gridParent.visible = visible;
      this.markRenderingNeedsUpdate();
    }
  }

  set axesHelperVisible(visible: boolean) {
    if (visible && !this.axesHelper) {
      this.axesHelper = new THREE.Group();
      const axisLength = 300;

      this.axesHelper.add(makeLine(0, 0, 0, axisLength, 0, 0, new THREE.LineBasicMaterial({ color: 0xff0000 })));
      this.axesHelper.add(makeLine(0, 0, 0, 0, axisLength, 0, new THREE.LineBasicMaterial({ color: 0x00ff00 })));
      this.axesHelper.add(makeLine(0, 0, 0, 0, 0, axisLength, new THREE.LineBasicMaterial({ color: 0x0000ff })));
      this.staticContent.add(this.axesHelper);
    }
    if (this.axesHelper) {
      this.axesHelper.visible = visible;
      this.markRenderingNeedsUpdate();
    }
  }

  /**
   * Setting an undefined material will remove the ground plane.
   */
  set groundPlaneMaterial(planeMat: THREE.Material | undefined) {
    if (!planeMat) {
      if (this.groundPlane) {
        this.staticContent.remove(this.groundPlane);
        this.groundPlane = undefined;
        if (this.ssfxRendererScene) {
          this.ssfxRendererScene.onRemove();
        }
        this.markRenderingNeedsUpdate();
      }
      return;
    }
    if (this.groundPlane) {
      this.groundPlane.material = planeMat;
      this.markRenderingNeedsUpdate();
      return;
    }
    this.groundPlane = new THREE.Mesh(groundPlaneGeo, planeMat);
    this.groundPlane.name = "groundplane";
    this.groundPlane.receiveShadow = true;
    this.groundPlane.position.z = -1;
    this.groundPlane.position.x = 100;
    this.groundPlane.updateMatrix();
    this.groundPlane.updateMatrixWorld();

    this.staticContent.add(this.groundPlane);
    if (this.ssfxRendererScene) {
      this.ssfxRendererScene.onAdd();
    }
    this.markRenderingNeedsUpdate();
  }

  /**
   * @param overlaySceneIndex Overlay scene unique id. Only set this if you want to remove from an overlay, leaving the parameter as undefined will remove from the base scene.
   */
  add(o: THREE.Object3D, overlaySceneId?: string) {
    if (overlaySceneId !== undefined) {
      const overlayScene = this.getOverlaySceneWithId(overlaySceneId);
      if (overlayScene) {
        overlayScene.scene.add(o);
      }
    } else {
      this.scene.add(o);
      if (this.ssfxRendererScene) {
        this.ssfxRendererScene.onAdd();
      }
    }
    this.markRenderingNeedsUpdate();
    return o;
  }

  /**
   * @param overlaySceneId Overlay scene unique id. Only set this if you want to remove from an overlay, leaving the parameter as undefined will remove from the base scene.
   */
  remove(o: THREE.Object3D, overlaySceneId?: string) {
    o.traverse(freeThreeObject3D);
    if (overlaySceneId !== undefined) {
      const overlayScene = this.getOverlaySceneWithId(overlaySceneId);
      if (overlayScene) {
        overlayScene.scene.remove(o);
      }
    } else {
      this.scene.remove(o);
      if (this.ssfxRendererScene) {
        this.ssfxRendererScene.onRemove();
      }
    }
    this.markRenderingNeedsUpdate();

    if (o === this.grid) {
      this.grid = null;
    }
  }

  getOverlaySceneWithId(overlaySceneId: string): OverlayScene | undefined {
    return this.overlayScenes.find((s) => s.id === overlaySceneId);
  }

  /**
   * Add an overlay scene.
   *
   * @param overlaySceneId A unique id for the scene. If it is a duplicate of an existing scene, the new scene will be ignored.
   * @param overlaySortIndex The sort index of the scene. The higher the sort index the closer to the camera the scene will be rendered.
   * @param worldScissorRect A scissor rect in scene world coordinates.
   * @returns
   */
  addOverlayScene(
    overlaySceneId: string,
    overlaySortIndex: number,
    worldScissorRect?: AABB2,
    clearStencil?: number,
  ): OverlayScene {
    const existingScene = this.getOverlaySceneWithId(overlaySceneId);
    if (existingScene) {
      console.warn(`duplicate overlay scene id (${overlaySceneId}) detected, ignoring.`);
      return existingScene;
    }

    const scene = new OverlayScene(overlaySceneId, overlaySortIndex, worldScissorRect, clearStencil);
    this.overlayScenes.push(scene);
    this.overlayScenes.sort((a, b) => {
      // we want a stable sort order if the sort index is the same
      if (a.sortIndex === b.sortIndex) {
        return a.id.localeCompare(b.id);
      }
      return a.sortIndex - b.sortIndex;
    });

    return scene;
  }

  /**
   * Removes the overlay scene with the id. Does nothing if the scene does not exist.
   */
  removeOverlayScene(overlaySceneId: string) {
    const idx = this.overlayScenes.findIndex((s) => s.id === overlaySceneId);
    if (idx !== -1) {
      const overlayScene = this.overlayScenes.splice(idx, 1)[0];
      clearThreeScene(overlayScene.scene);
    }
  }

  clear(clearStaticContent: boolean = false) {
    clearThreeScene(this.scene, clearStaticContent ? undefined : this.staticContent);
    if (this.ssfxRendererScene) {
      this.ssfxRendererScene.onClear();
    }
    for (const overlayScene of this.overlayScenes) {
      clearThreeScene(overlayScene.scene);
    }

    this.grid = null;

    this.markRenderingNeedsUpdate();
  }

  /**
   * @param background Background color in light mode.
   * @param darkModeBackground The background as it should appear in dark mode (after rest of the colors are inverted).
   * The value is quantized to inverse SRGB space, so it is not fully accurate for dark colors. By default dark mode uses inverted light mode background.
   */
  setBackground(background: THREE.Color | null, darkModeBackground?: THREE.Color | null) {
    this.background = background;
    this.darkModeBackground = darkModeBackground;
  }

  applyBackgroundForRender() {
    this.scene.background = this.background;
    if (this.darkMode && this.darkModeBackground !== undefined) {
      if (this.darkModeBackground === null) {
        this.scene.background = null;
      } else {
        this.scene.background = new THREE.Color(this.darkModeBackground);
        this.scene.background.r = 1 - this.scene.background.r;
        this.scene.background.g = 1 - this.scene.background.g;
        this.scene.background.b = 1 - this.scene.background.b;
      }
    }
    if (this.grid2DMaterial) {
      const color = this.darkMode ? new THREE.Color(0.75, 0.75, 0.75) : new THREE.Color(0.4, 0.4, 0.4);
      this.grid2DMaterial.uniforms.color.value.set(color);
    }
  }

  /**
   * Setup overlay passes for rendering, expected to be called by the renderer once per frame.
   */
  setupOverlayPasses(
    overlayPasses: EffectComposerPass[],
    camera: THREE.Camera,
    worldAABBToPixelAABB?: (worldAABB: AABB2) => THREE.Box2,
  ) {
    for (const overlayScene of this.overlayScenes) {
      overlayScene.setCamera(camera);
    }
    overlayPasses.splice(
      0,
      overlayPasses.length,
      ...this.overlayScenes.map((overlayScene) => overlayScene.setupRenderPass(worldAABBToPixelAABB)),
    );
  }

  setDarkMode(enabled: boolean) {
    if (this.darkMode === enabled) {
      return;
    }
    this.darkMode = enabled;
    this.markRenderingNeedsUpdate();
  }

  getDarkMode() {
    return this.darkMode;
  }

  dispose() {}
}
